import { Link, useLocation } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { Button } from "antd";
import { isRouteAllowed } from "commons/helper";
import { sideMenuConfig } from "../../routing";
import useAuthStore from "stores/AuthStore/AuthStore";  
import useDiscountCodesConfig from "hooks/useDiscountCodesConfig/useDiscountCodesConfig";

const BASE_PATH = "/shoup-community";

const getMenuItems = ({ sideMenuConfig, isRootUser, isOwner, codesEnabled }) => {
  return sideMenuConfig
    .filter((item) => !item.hideInMenu)
    .filter((item) =>
      isRouteAllowed(item, { isRootUser, isOwner, codesEnabled })
    )
    .map((item) => {
      const normalizedPath = item.path?.startsWith("/")
        ? item.path.slice(1)
        : item.path || "";
      const fullPath = `${BASE_PATH}/${normalizedPath}`;
      return {
        label: item.label,
        icon: item.icon?.type || (() => null),
        path: fullPath,
      };
    });
};

const Navigation = ({
  isCollapsed,
  setIsCollapsed,
  isMobileOpen,
  setIsMobileOpen,
}) => {
  const location = useLocation();
  const { isRootUser, isOwner } = useAuthStore();
  const { enabled: codesEnabled } = useDiscountCodesConfig();
  const menuItems = getMenuItems({
    sideMenuConfig,
    isRootUser,
    isOwner,
    codesEnabled,
  });

  const sidebarContent = (
    <div className="flex h-full flex-col bg-card border-r border-border/50 transition-all duration-300">
      <div className="flex h-16 items-center justify-between px-4 border-b border-border/50">
        {isCollapsed ? (
          <Link to={`${BASE_PATH}/dashboard`} className="mx-auto flex-shrink-0">
            <img src="/shoup.svg" alt="ShoUp" className="h-8 w-8 rounded-lg" />
          </Link>
        ) : (
          <Link to={`${BASE_PATH}/dashboard`} className="flex items-center space-x-2.5">
            <img
              src="/shoup.svg"
              alt="ShoUp"
              className="h-8 w-8 rounded-lg flex-shrink-0"
            />
            <span className="text-sm font-bold tracking-tight truncate">
              ShoUp
            </span>
          </Link>
        )}
        <Button
          type="text"
          size="small"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="hidden lg:flex flex-shrink-0"
        >
          <ChevronLeft
            className={`h-4 w-4 transition-transform ${isCollapsed ? "rotate-180" : ""}`}
          />
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto py-3">
        <nav className="space-y-0.5 px-2">
          {menuItems.map((item) => {
            const ItemIcon = item.icon;
            const isActive =
              location.pathname === item.path ||
              location.pathname.startsWith(item.path + "/");
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMobileOpen(false)}
                className={`flex items-center rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? "bg-foreground text-background"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                <ItemIcon
                  className={`h-[18px] w-[18px] shrink-0 ${isCollapsed ? "mx-auto" : "mr-3"}`}
                />
                {!isCollapsed && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 hidden lg:block ${
          isCollapsed ? "w-16" : "w-64"
        } transition-all duration-300`}
      >
        {sidebarContent}
      </aside>

      {/* Mobile Sidebar Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-foreground/20 backdrop-blur-sm lg:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 lg:hidden ${
          isMobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {sidebarContent}
      </aside>
    </>
  );
};

export default Navigation;
